<script>
import { defineComponent } from "vue";
import UsersMixin from "@/views/users/UsersMixin.vue";
import DatePicker from "@/components/pickers/DatePicker.vue";
import BaseView from "@/views/BaseView.vue";
import SearchField from "@/components/inputs/SearchField.vue";
import UserSelect from "@/views/users/components/UserSelect.vue";
import TableFilter from "@/components/tableFilter/TableFilter.vue";
import TableFilterRow from "@/components/tableFilter/TableFilterRow.vue";
import OmniSelect from "@/components/controls/OmniSelect.vue";
import { MasksMixin } from "@/mixins/masks.mixin.js";
import StateField from "@/components/tableFilter/fields/StateField.vue";
import ZipField from "@/components/tableFilter/fields/ZipField.vue";

const DriverDocument = {
  license: "license",
  certificate: "certificate",
  citizenship: "citizenship",
};

export default defineComponent({
  name: "DriversSearchForm",
  components: { ZipField, StateField, OmniSelect, TableFilterRow, TableFilter, SearchField, DatePicker, UserSelect },
  mixins: [BaseView, UsersMixin, MasksMixin],
  emits: ["search"],
  data() {
    return {
      params: {},
      showAdditionalFilters: false,
    };
  },
  computed: {
    documentTypes() {
      return [
        {
          name: this.$t("drivers.driver-license"),
          type: DriverDocument.license,
          value: "driver_license",
        },
        { divider: true },
        { header: this.$t("drivers.certificates") },
        {
          name: this.$t("drivers.twic"),
          type: DriverDocument.certificate,
          value: "certificate_twic",
        },
        {
          name: this.$t("drivers.tsa"),
          type: DriverDocument.certificate,
          value: "certificate_tsa",
        },
        {
          name: this.$t("drivers.tanker"),
          type: DriverDocument.certificate,
          value: "certificate_tanker",
        },
        {
          name: this.$t("drivers.hazmat"),
          type: DriverDocument.certificate,
          value: "certificate_hazmat",
        },
        { header: this.$t("drivers.citizenship") },
        ...this.citizenships.map((c) => {
          c.type = DriverDocument.citizenship;
          c.value = c.id;
          return c;
        }),
      ];
    },
    isDocumentSelected() {
      return this.params.documentType;
    },
  },
  created() {
    this.params = this.getSearchParams("drivers");
    this.search();
  },
  methods: {
    clear() {
      this.params = {};
      this.search();
    },
    search() {
      this.setSearchParams("drivers", this.params);
      this.$emit("search", this.params);
    },
    documentChanged(doc) {
      this.clearDocument();
      if (doc) {
        if (doc.type === DriverDocument.license || doc.type === DriverDocument.certificate) {
          this.params[doc.value] = true;
        }
        if (doc.type === DriverDocument.citizenship) {
          this.params[doc.type] = doc.value;
        }
      }
      this.search();
    },
    clearDocument() {
      this.documentTypes.forEach((doc) => {
        if (doc.type === DriverDocument.license || doc.type === DriverDocument.certificate) {
          delete this.params[doc.value];
        }
        if (doc.type === DriverDocument.citizenship) {
          delete this.params[doc.type];
        }
      });
    },
  },
});
</script>

<template>
  <table-filter
    :filters="params"
    :header-filter-keys="['name', 'status', 'owner', 'phone', 'truck', 'email']"
    @apply="search"
    @clear="clear"
  >
    <template #header>
      <table-filter-row :divider="false" :label="$t('general.filter-main')" icon="mdi-filter-variant">
        <v-col>
          <search-field id="search--name-field" v-model="params.name" :label="$t('general.name')" @search="search" />
        </v-col>
        <v-col>
          <omni-select
            id="search--status-select"
            v-model="params.status"
            :clearable="true"
            :items="driverStatuses"
            :label="$t('general.status')"
            color="primary"
            density="compact"
            hide-details
            item-title="name"
            item-value="id"
            variant="outlined"
            @update:model-value="search"
          />
        </v-col>
        <v-col>
          <user-select
            id="search--owner-select"
            v-model="params.owner"
            :label="$t('users.owner')"
            owners
            @change="search"
          />
        </v-col>
        <v-col>
          <search-field id="search--truck-field" v-model="params.truck" :label="$t('drivers.truck')" @search="search" />
        </v-col>
        <v-col>
          <search-field
            id="search--phone-field"
            v-model="params.phone"
            :label="$t('general.phone')"
            :mask="phoneMask"
            @search="search"
          />
        </v-col>
        <v-col>
          <search-field
            id="search--email-field"
            v-model="params.email"
            :label="$t('general.email')"
            type="email"
            @search="search"
          />
        </v-col>
      </table-filter-row>
    </template>
    <table-filter-row :label="$t('general.address')" icon="mdi-map-marker-radius-outline">
      <v-col>
        <search-field id="search--city-field" v-model="params.city" :label="$t('general.city')" @search="search" />
      </v-col>
      <v-col>
        <state-field v-model="params.state" @search="search" />
      </v-col>
      <v-col>
        <zip-field v-model="params.zip" @search="search" />
      </v-col>
    </table-filter-row>
    <table-filter-row :divider="false" :label="$t('general.documents')" icon="mdi-badge-account-horizontal-outline">
      <v-col cols="4">
        <omni-select
          id="search--document-select"
          v-model="params.documentType"
          :clearable="true"
          :items="documentTypes"
          :label="$t('general.type')"
          item-value="value"
          return-object
          @update:model-value="documentChanged"
        >
        </omni-select>
      </v-col>
      <v-col cols="4">
        <v-row>
          <v-col>
            <date-picker
              id="search--document-expiration-date-from"
              v-model="params.documentExpirationDateFrom"
              :label="$t('general.expirationDateFrom')"
            />
          </v-col>

          <v-col>
            <date-picker
              id="search--document-expiration-date-to"
              v-model="params.documentExpirationDateTo"
              :label="$t('general.expirationDateTo')"
            />
          </v-col>
        </v-row>
      </v-col>
    </table-filter-row>
  </table-filter>
</template>
